import { useDemo } from "../zustand/DemoZustand";
import { Abschnitt } from "./Grundelemente";
import { Daumen, DaumenRunter, Fragezeichen, Waage } from "./Symbole";
import {
  AMPEL,
  POSITION_TEXT,
  meinungsbildFuerTop,
  meineMeinung,
  nameVon,
} from "../hilfen/auswertung";
import { uhrzeit, wochentag } from "../hilfen/datum";

/*
  Das Meinungsbild der Fraktion zu einem Tagesordnungspunkt.

  Mit Ampelfarben — grün, gelb, rot —, weil das Zeichensystem des Ratssaals
  älter und stärker ist als jede Parteizuordnung. Die Position steht trotzdem
  immer als Wort da: Rot-Grün ist die häufigste Farbfehlsichtigkeit, und eine
  Aussage, die nur in der Farbe steckt, wäre für rund jeden zwölften Mann
  nicht lesbar.

  Und bewusst „Meinungsbild“, nie „Abstimmung“: Die Software ist vorbereitend,
  nicht rechtsverbindlich. Beschlossen wird in der Sitzung des Gremiums.
*/

export default function Meinungsbild({ top }) {
  const { stammdaten, zustand, ich, aktionen } = useDemo();
  const sitzung = stammdaten.sitzungen[top.sitzungId];
  const mb = meinungsbildFuerTop(zustand, stammdaten, top.id);
  const meine = meineMeinung(zustand, ich.id, top.id);

  const SYMBOL = { dafuer: Daumen, unentschieden: Waage, dagegen: DaumenRunter };

  const gruppen = [
    ...AMPEL.map((a) => ({
      schluessel: a.position,
      ampel: a.ampel,
      titel: POSITION_TEXT[a.position],
      symbol: SYMBOL[a.position],
      ids: mb[a.position],
    })),
    { schluessel: "offen", titel: "Noch keine Rückmeldung", symbol: Fragezeichen, ids: mb.offen },
  ];

  return (
    <>
      <p className="meinungsbild__hinweis">
        <Waage groesse={20} />
        <span>
          <strong>Meinungsbild, keine Beschlussfassung.</strong> Es dient der Vorbereitung der
          Fraktionssitzung. Abgestimmt wird in der Sitzung des Gremiums. Änderbar bis
          Sitzungsbeginn.
        </span>
      </p>

      <div className="karte" style={{ marginBottom: "var(--luft-3)" }}>
        <p style={{ fontWeight: 600 }}>{meine ? "Deine Position" : "Wie stehst du dazu?"}</p>
        {/*
          Reihenfolge wie an einer Ampel: Zustimmung, Enthaltung, Ablehnung.
          Das Wort steht immer dabei — die Farbe beschleunigt das Finden, sie
          trägt die Aussage nicht.
        */}
        <div className="wahl">
          {AMPEL.map((a) => {
            const Symbol = SYMBOL[a.position];
            return (
              <button
                key={a.position}
                className={`wahl__knopf wahl__knopf--${a.ampel}`}
                aria-pressed={meine === a.position}
                onClick={() => aktionen.setzeMeinungsbild(top.id, a.position)}
              >
                <Symbol groesse={18} />
                {POSITION_TEXT[a.position]}
              </button>
            );
          })}
        </div>

        <p className="klein leise" style={{ marginTop: "var(--luft-3)" }}>
          Änderbar bis Sitzungsbeginn am {wochentag(sitzung.beginn)}, {uhrzeit(sitzung.beginn)}.
          Innerhalb der Fraktion ist deine Position namentlich sichtbar.
        </p>

        {meine && (
          <button
            className="knopf knopf--schlicht klein"
            style={{ paddingLeft: 0, minHeight: "2.5rem" }}
            onClick={() => aktionen.entferneMeinungsbild(top.id)}
          >
            Position zurückziehen
          </button>
        )}
      </div>

      <div className="karte">
        {mb.anzahlGeaeussert === 0 ? (
          <p className="leise">Zu diesem Punkt hat sich noch niemand aus der Fraktion geäußert.</p>
        ) : (
          gruppen
            .filter((g) => g.ids.length > 0)
            .map((g) => (
              <div
                key={g.schluessel}
                className={`gruppe ${g.schluessel === "offen" ? "gruppe--offen" : ""} ${
                  g.ampel ? `gruppe--${g.ampel}` : ""
                }`}
              >
                <p className="gruppe__titel">
                  <g.symbol groesse={18} />
                  {g.titel} · {g.ids.length}
                </p>
                <div className="namen">
                  {g.ids.map((id) => (
                    <span key={id} className={`name ${id === ich.id ? "name--ich" : ""}`}>
                      {nameVon(stammdaten, id, ich.id)}
                    </span>
                  ))}
                </div>
              </div>
            ))
        )}
      </div>
    </>
  );
}

/* Die Fragen, die Mitglieder für die Fraktionssitzung eingereicht haben. */
export function Fragenliste({ fragen }) {
  const { stammdaten, ich } = useDemo();
  if (fragen.length === 0) return null;

  return (
    <Abschnitt
      titel={fragen.length === 1 ? "Eingereichte Frage" : "Eingereichte Fragen"}
      hinweis="Für die Fraktionssitzung eingereicht. Wird dort besprochen."
    >
      {fragen.map((f) => (
        <div key={f.id} className="frage">
          <p>„{f.text}“</p>
          <p className="frage__wer">
            {nameVon(stammdaten, f.verfasserId, ich.id)} · vor {f.eingereichtVorTagen} Tagen
          </p>
        </div>
      ))}
    </Abschnitt>
  );
}
